/** Curves adjustment — pure math + types, no React, no Fabric.
 *
 *  Lives outside the React component so the renderer-bound parts
 *  (`AdjustmentsModal`, `CurvesPanel`) can import it without dragging
 *  any UI deps, and so the same math can later be reused by the
 *  non-destructive adjustment-layer slow path. */

export type CurvePoint = { x: number; y: number };
export type CurveChannel = 'rgb' | 'r' | 'g' | 'b';

export interface CurvesValue {
  rgb: CurvePoint[];
  r: CurvePoint[];
  g: CurvePoint[];
  b: CurvePoint[];
}

/** Identity curve — endpoints at (0,0) and (255,255), nothing in
 *  between. Used as the starting state for each channel. */
export const identityCurve = (): CurvePoint[] => [
  { x: 0, y: 0 },
  { x: 255, y: 255 },
];

export const identityCurvesValue = (): CurvesValue => ({
  rgb: identityCurve(),
  r: identityCurve(),
  g: identityCurve(),
  b: identityCurve(),
});

/** Build a 256-entry LUT from a sorted list of curve points using
 *  linear interpolation between consecutive points. Cubic bezier
 *  smoothing was considered but linear matches the visual line drawn
 *  on the editor — what you see is what you get. */
export function buildLUT(points: CurvePoint[]): Uint8ClampedArray {
  const lut = new Uint8ClampedArray(256);
  // Defensive against an empty `points` array (the dialog's UI
  // can't produce one — endpoints aren't deletable — but a future
  // deserialisation / preset / plugin caller might). Identity LUT
  // is the safest fallback: leaves the channel unchanged.
  if (points.length === 0) {
    for (let x = 0; x < 256; x++) lut[x] = x;
    return lut;
  }
  const sorted = [...points].sort((a, b) => a.x - b.x);
  for (let x = 0; x < 256; x++) {
    if (x <= sorted[0].x) {
      lut[x] = sorted[0].y;
    } else if (x >= sorted[sorted.length - 1].x) {
      lut[x] = sorted[sorted.length - 1].y;
    } else {
      for (let i = 0; i < sorted.length - 1; i++) {
        if (sorted[i].x <= x && sorted[i + 1].x >= x) {
          const span = sorted[i + 1].x - sorted[i].x;
          const t = span > 0 ? (x - sorted[i].x) / span : 0;
          lut[x] = Math.round(sorted[i].y + t * (sorted[i + 1].y - sorted[i].y));
          break;
        }
      }
    }
  }
  return lut;
}

/** Compose the channel curves: a pixel's R goes through the combined
 *  RGB curve first, then the per-channel R curve (Photoshop's model).
 *  Source is `ImageData` rather than a canvas so callers can cache
 *  the original pixels once and re-apply curves repeatedly during
 *  live preview without paying for a getImageData round-trip every
 *  drag tick. */
/** Pure pixel transform — returns a fresh ImageData. Tests use this
 *  variant; the canvas-bound `applyCurvesToCanvas` is just a thin
 *  putImageData wrapper around it. */
export function applyCurvesPixels(source: ImageData, value: CurvesValue): ImageData {
  const w = source.width;
  const h = source.height;
  const lutRGB = buildLUT(value.rgb);
  const lutR = buildLUT(value.r);
  const lutG = buildLUT(value.g);
  const lutB = buildLUT(value.b);
  const sd = source.data;
  const out = new Uint8ClampedArray(sd.length);
  for (let i = 0; i < sd.length; i += 4) {
    out[i] = lutR[lutRGB[sd[i]]];
    out[i + 1] = lutG[lutRGB[sd[i + 1]]];
    out[i + 2] = lutB[lutRGB[sd[i + 2]]];
    out[i + 3] = sd[i + 3];
  }
  return new ImageData(out, w, h);
}

export function applyCurvesToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: CurvesValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyCurvesPixels(source, value), 0, 0);
}

/** Compute a 256-bucket histogram of the source canvas's R/G/B
 *  channel values (combined as luminance proxy: just the average).
 *  Used to draw a soft backdrop in the curve editor so the user
 *  knows where the image data sits.
 *
 *  Sampled down to 256×256 for speed on large images — we only need
 *  buckets, not pixel-perfect counts. */
export function computeHistogram(source: HTMLCanvasElement): number[] {
  const ctx = source.getContext('2d');
  if (!ctx) return new Array(256).fill(0);
  const sampleW = Math.min(source.width, 256);
  const sampleH = Math.min(source.height, 256);
  const tmp = document.createElement('canvas');
  tmp.width = sampleW;
  tmp.height = sampleH;
  const tCtx = tmp.getContext('2d', { willReadFrequently: true });
  if (!tCtx) return new Array(256).fill(0);
  tCtx.drawImage(source, 0, 0, sampleW, sampleH);
  const imageData = tCtx.getImageData(0, 0, sampleW, sampleH);
  return histogramFromImageData(imageData);
}

/** Variant that takes raw `ImageData` rather than a canvas. Used by
 *  the adjustments modal so the histogram reflects the *source*
 *  pixels we're about to edit, not the live target canvas (which
 *  may already hold previously-baked adjustments after consecutive
 *  Apply rounds). */
export function computeHistogramFromImageData(source: ImageData): number[] {
  // Downsample if the source is large so the bucket count stays
  // cheap on multi-MP images.
  const w = source.width;
  const h = source.height;
  const longEdge = Math.max(w, h);
  if (longEdge <= 256) return histogramFromImageData(source);
  const sampleW = Math.min(w, 256);
  const sampleH = Math.min(h, 256);
  const tmp = document.createElement('canvas');
  tmp.width = w;
  tmp.height = h;
  const tCtx = tmp.getContext('2d', { willReadFrequently: true });
  if (!tCtx) return new Array(256).fill(0);
  tCtx.putImageData(source, 0, 0);
  const small = document.createElement('canvas');
  small.width = sampleW;
  small.height = sampleH;
  const sCtx = small.getContext('2d', { willReadFrequently: true });
  if (!sCtx) return new Array(256).fill(0);
  sCtx.drawImage(tmp, 0, 0, sampleW, sampleH);
  return histogramFromImageData(sCtx.getImageData(0, 0, sampleW, sampleH));
}

function histogramFromImageData(imageData: ImageData): number[] {
  const data = imageData.data;
  const hist = new Array(256).fill(0);
  for (let i = 0; i < data.length; i += 4) {
    const v = Math.round((data[i] + data[i + 1] + data[i + 2]) / 3);
    hist[v]++;
  }
  return hist;
}
